import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircleRounded,
  DashboardRounded,
  DownloadRounded,
  ErrorRounded,
  HomeRounded,
  RefreshRounded,
  SupportRounded,
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { backend_api } from "../static/server";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { formattedDate } from "../styles/styles";
import logo from "../assets/nav-lexmom.webp";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getMySubscription } from "../redux/Apps/users/userSlice";
import { toast } from "react-toastify";
import { isDevMode } from "../components/Auth/SubscriptionContext";
import {
  applyAuthSession,
  clearPendingAuthSession,
  getPendingAuthSession,
  getStoredAuthToken,
} from "../redux/Apps/users/authSession";

const PaymentStatusPage = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.user);

  const paymentId =
    searchParams.get("payment_id") ||
    searchParams.get("razorpay_payment_id") ||
    null;

  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [finalizingSession, setFinalizingSession] = useState(false);
  const confirmedPaymentRef = useRef(null);
  const continueInFlightRef = useRef(false);
  const autoContinueAttemptedRef = useRef(false);

  const paymentSessionToken = useMemo(() => {
    const storedToken = getStoredAuthToken();
    const pendingToken = getPendingAuthSession()?.token || null;

    return token || storedToken || pendingToken || null;
  }, [token]);

  const corePaymentData = useMemo(
    () => paymentData?.data || paymentData || {},
    [paymentData],
  );

  const setupSessionFromPayment = useCallback(
    (sessionPayload) =>
      applyAuthSession(dispatch, sessionPayload, {
        clearPending: true,
      })?.tokenValue || null,
    [dispatch],
  );

  const syncSubscriptionState = useCallback(
    async (activeToken) => {
      let lastError = null;

      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          await dispatch(
            getMySubscription({ token: activeToken, force: true }),
          ).unwrap();
          return true;
        } catch (syncError) {
          lastError = syncError;

          if (attempt < 3) {
            await new Promise((resolve) => window.setTimeout(resolve, 1200));
          }
        }
      }

      throw lastError;
    },
    [dispatch],
  );

  const getPaymentId = () => corePaymentData?.payment_id || paymentId || "N/A";

  const getPaymentMethod = () => {
    const method = corePaymentData?.payment_method?.toLowerCase() || "unknown";

    // Check payment ID pattern to detect method
    const paymentId = getPaymentId();
    if (paymentId.startsWith("pay_")) return "razorpay";
    if (paymentId.startsWith("cs_test_") || paymentId.startsWith("cs_live_"))
      return "stripe";

    return method;
  };

  const paymentMethod = getPaymentMethod();
  const applied_coupon = corePaymentData?.coupon_codes;

  // Simplified data extraction functions
  const getPaymentAmount = () => {
    if (paymentMethod === "stripe") {
      return (corePaymentData?.payment_response?.amount_total || 0) / 100;
    }
    return corePaymentData?.amount || 0;
  };

  const getPaymentCurrency = () => {
    if (paymentMethod === "stripe") {
      return (
        corePaymentData?.payment_response?.currency?.toUpperCase() || "USD"
      );
    }
    return corePaymentData?.payment_response?.currency || "INR";
  };

  const getPaidAt = () => {
    if (paymentMethod === "stripe") {
      // Convert Stripe timestamp to ISO string
      const created = corePaymentData?.payment_response?.created;
      if (created) {
        return new Date(created * 1000).toISOString();
      }
    }
    return (
      corePaymentData?.paid_at || corePaymentData?.order?.created_at || null
    );
  };

  const finalStatus = useMemo(() => {
    const resolvedStatus =
      paymentMethod === "stripe"
        ? corePaymentData?.payment_response?.payment_status === "paid"
          ? "paid"
          : corePaymentData?.payment_response?.payment_status ||
            corePaymentData?.status ||
            "unknown"
        : corePaymentData?.status ||
          corePaymentData?.payment_response?.status ||
          "unknown";

    const status = String(resolvedStatus || "").toLowerCase();

    // Expanded success statuses for both Razorpay and Stripe
    if (
      ["captured", "paid", "success", "complete", "succeeded"].includes(status)
    )
      return "success";
    if (status === "failed") return "failure";
    if (["authorized", "authorised", "pending"].includes(status))
      return "authorized";
    return "error";
  }, [corePaymentData, paymentMethod]);

  useEffect(() => {
    let isMounted = true;
    let receiptTimer = null;

    const confirmPayment = async () => {
      if (!paymentId) {
        setError("Payment ID not found in URL");
        setLoading(false);
        return;
      }

      if (confirmedPaymentRef.current === paymentId) {
        return;
      }

      confirmedPaymentRef.current = paymentId;

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const config = { timeout: 10000 };
        if (paymentSessionToken) {
          config.headers = { Authorization: `Token ${paymentSessionToken}` };
        }

        const response = await axios.get(
          `${backend_api}confirm-payment/?razorpay_payment_id=${paymentId}`,
          config,
        );

        if (response.data) {
          if (isMounted) {
            setPaymentData(response.data.data || response.data);
          }
        } else {
          throw new Error("No payment data received from server");
        }
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to confirm payment. Please contact support.";
        if (isMounted) {
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          receiptTimer = window.setTimeout(() => setShowReceipt(true), 500);
        }
      }
    };

    confirmPayment();
    return () => {
      isMounted = false;
      if (receiptTimer) {
        window.clearTimeout(receiptTimer);
      }
    };
  }, [paymentId, paymentSessionToken]);

  const formatAmount = (amount, currency = "INR") => {
    try {
      const numAmount =
        typeof amount === "string" ? parseFloat(amount) : Number(amount);
      if (Number.isNaN(numAmount) || numAmount <= 0) return "N/A";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency === "USD" ? "USD" : "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    } catch {
      return "N/A";
    }
  };

  const formatDate = (dateString) =>
    dateString ? formattedDate(dateString) : "N/A";

  const statusConfig = useMemo(
    () => ({
      success: {
        icon: CheckCircleRounded,
        color: theme.palette.success.main,
        title: "Payment Successful!",
        subtitle: "Your subscription is now active",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.success.main,
          0.1,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
        border: alpha(theme.palette.success.main, 0.2),
      },
      authorized: {
        icon: RefreshRounded,
        color: theme.palette.warning.main,
        title: "Payment Authorized",
        subtitle: "Awaiting capture from payment gateway",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.warning.main,
          0.1,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
        border: alpha(theme.palette.warning.main, 0.2),
      },
      failure: {
        icon: ErrorRounded,
        color: theme.palette.error.main,
        title: "Payment Failed",
        subtitle: "Your subscription has not been activated",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.error.main,
          0.1,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
        border: alpha(theme.palette.error.main, 0.2),
      },
      error: {
        icon: ErrorRounded,
        color: theme.palette.error.main,
        title: "Something went wrong",
        subtitle: "We could not confirm your payment",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.error.main,
          0.08,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
        border: alpha(theme.palette.error.main, 0.2),
      },
    }),
    [theme],
  );

  const handleContinue = useCallback(
    async ({ silent = false } = {}) => {
      if (continueInFlightRef.current) {
        return false;
      }

      continueInFlightRef.current = true;
      setFinalizingSession(true);

      try {
        let activeToken = token || getStoredAuthToken();

        if (!activeToken) {
          const pendingSession = getPendingAuthSession();

          if (pendingSession) {
            activeToken = setupSessionFromPayment(pendingSession);
          }
        }

        if (!activeToken) {
          if (!silent) {
            toast.error("No user data found. Please login.");
          }
          navigate("/login", { replace: true });
          return false;
        }

        clearPendingAuthSession();
        await syncSubscriptionState(activeToken);

        if (!silent) {
          toast.success(
            "Success! Your subscription is active. Redirecting you to your dashboard...",
          );
        }

        navigate("/dashboard", { replace: true });
        return true;
      } catch (continuationError) {
        console.error("Error in handleContinue:", continuationError);

        if (!silent) {
          toast.error("Failed to setup session. Please try again.");
        }

        return false;
      } finally {
        continueInFlightRef.current = false;
        setFinalizingSession(false);
      }
    },
    [navigate, setupSessionFromPayment, syncSubscriptionState, token],
  );

  useEffect(() => {
    const hasRecoverableSession = Boolean(
      token || getPendingAuthSession() || getStoredAuthToken(),
    );

    if (
      loading ||
      finalStatus !== "success" ||
      !hasRecoverableSession ||
      autoContinueAttemptedRef.current
    ) {
      return;
    }

    autoContinueAttemptedRef.current = true;

    handleContinue({ silent: true }).then((didRedirect) => {
      if (!didRedirect) {
        autoContinueAttemptedRef.current = false;
      }
    });
  }, [finalStatus, handleContinue, loading, token]);

  const handleTryAgain = () => navigate("/pricing");
  const handleContactSupport = () => navigate("/support");

  const config = statusConfig[finalStatus] || statusConfig.error;
  const Icon = config.icon;

  const PaymentSummary = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: config.gradient,
          width: "100%",
          maxWidth: "500px",
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${alpha(
                  config.color,
                  0.2,
                )}, ${alpha(config.color, 0.1)})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: 32, color: config.color }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {formatAmount(getPaymentAmount(), getPaymentCurrency())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payment {finalStatus === "success" ? "completed" : finalStatus}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Payment ID
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  wordBreak: "break-all",
                  fontSize: "0.85rem",
                }}
              >
                {getPaymentId()}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Date
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDate(getPaidAt())}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Method
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                textTransform="capitalize"
              >
                {paymentMethod}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Applied coupon
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                textTransform="capitalize"
              >
                {applied_coupon || "None"}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>
    </motion.div>
  );

  const getStatusMessage = () => {
    switch (finalStatus) {
      case "success":
        return "Thank you for your payment! Your subscription has been activated successfully. You can now access all features.";
      case "authorized":
        return "Your payment has been authorized and is awaiting capture. This usually takes a few moments. You'll receive a confirmation email once completed.";
      case "failure":
        return (
          error ||
          "We couldn't process your payment. This may be due to insufficient funds, network issues, or incorrect payment details."
        );
      default:
        return (
          error ||
          "We were unable to verify your payment status. Please check your payment history or contact support for assistance."
        );
    }
  };

  const getActions = () => {
    switch (finalStatus) {
      case "success":
        return [
          <Button
            key="dashboard"
            fullWidth
            variant="contained"
            startIcon={<DashboardRounded />}
            onClick={handleContinue}
            disabled={finalizingSession}
            sx={{ borderRadius: 2 }}
          >
            {finalizingSession ? "Finalizing your access..." : "Continue with dashboard"}
          </Button>,
          <Button
            key="support"
            fullWidth
            variant="outlined"
            onClick={handleContactSupport}
            sx={{ borderRadius: 2 }}
          >
            Contact Support
          </Button>,
        ];
      case "authorized":
        return [
          <Button
            key="support"
            fullWidth
            variant="outlined"
            startIcon={<SupportRounded />}
            onClick={handleContactSupport}
            sx={{ borderRadius: 2 }}
          >
            Contact Support
          </Button>,
        ];
      case "failure":
        return [
          <Button
            key="retry"
            fullWidth
            variant="contained"
            onClick={handleTryAgain}
            sx={{ borderRadius: 2 }}
          >
            Try Again
          </Button>,
          <Button
            key="support"
            fullWidth
            variant="outlined"
            startIcon={<SupportRounded />}
            onClick={handleContactSupport}
            sx={{ borderRadius: 2 }}
          >
            Contact Support
          </Button>,
        ];
      default:
        return [
          <Button
            key="retry"
            variant="contained"
            onClick={() => navigate("/")}
            startIcon={<HomeRounded />}
          >
            Go to home
          </Button>,
          <Button
            key="support"
            variant="outlined"
            startIcon={<SupportRounded />}
            onClick={handleContactSupport}
          >
            Contact Support
          </Button>,
        ];
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Confirming your payment...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: { xs: "1rem", md: "2rem" },
        flexDirection: { xs: "column", lg: "row" },
        p: "1rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "1rem",
          width: "100%",
          flex: 1,
          p: "2rem",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <Chip
              label={finalStatus.toUpperCase()}
              sx={{
                letterSpacing: 0.5,
                backgroundColor: alpha(config.color, 0.1),
                color: config.color,
                fontWeight: "600",
                width: "fit-content",
              }}
            />
            <Typography
              variant="body1"
              sx={{ fontSize: "1.4rem", fontWeight: "600" }}
            >
              {config.title}
            </Typography>
            <Typography
              variant="body"
              sx={{ fontSize: "1rem", color: "text.secondary" }}
            >
              {config.subtitle}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              {getStatusMessage()}
            </Typography>
          </Box>
        </motion.div>

        <PaymentSummary />

        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          {getActions()}
        </Box>
      </Box>

      {/* Receipt section - only show for successful payments */}
      {finalStatus === "success" && (
        <Box sx={{ width: "100%", p: "2rem", flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <AnimatePresence mode="wait">
              {showReceipt ? (
                <motion.div
                  key="receipt"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  {paymentMethod === "stripe" ? (
                    <StripeReceiptCard
                      data={corePaymentData}
                      background={config.gradient}
                    />
                  ) : (
                    <RazorpayReceiptCard
                      data={corePaymentData}
                      background={config.gradient}
                    />
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="receipt-loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Box
                    sx={{
                      height: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Box>
      )}
    </Box>
  );
};

export default PaymentStatusPage;

// Utility functions
const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number(n || 0),
  );

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(n || 0),
  );

// Stripe Receipt Component
const StripeReceiptCard = ({ data, background }) => {
  const receiptRef = useRef();

  const r = useMemo(() => {
    if (!data) return null;

    const pr = data.payment_response || {};
    const customerDetails = pr.customer_details || {};
    const order = data.order || {};
    const metadata = pr.metadata || {};

    // Amount in USD (Stripe amounts are in cents)
    const amountUsd = pr.amount_total
      ? pr.amount_total / 100
      : Number(data.amount || order.total_amount || 0);

    // Payment method
    const paymentMethod = pr.payment_method_types?.[0] || "card";

    // Paid At (convert Stripe timestamp to date)
    const paidAt = pr.created
      ? new Date(pr.created * 1000)
      : new Date(data.paid_at || order.created_at);
    const paidAtStr = paidAt.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    // Payer Name
    const payerName =
      customerDetails.name ||
      `${order?.lawyer?.first_name || ""} ${
        order?.lawyer?.last_name || ""
      }`.trim() ||
      order?.lawyer?.reminder_mail ||
      "—";

    const mobile = order?.lawyer?.mobile || "—";
    const email = customerDetails.email || order?.lawyer?.reminder_mail || "—";

    return {
      totalAmount: amountUsd,
      currency: pr.currency?.toUpperCase() || "USD",
      refNumber: data.payment_id || pr.id || "—",
      paymentTime: paidAtStr,
      paymentMethod:
        paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1),
      payerName,
      mobile,
      email,
      receiptNumber:
        pr.invoice ||
        `STR-${String(data.payment_id || pr.id || Date.now()).slice(-8)}`,
      orderId: order?.id || metadata.order_id || "—",
      status: (pr.payment_status || data.status || "—").toLowerCase(),
      plan:
        order?.item?.subscription_plan?.name ||
        metadata.subscription_plan ||
        "—",
      billingCycle: order?.billing_cycle || metadata.billing_cycle || "—",
      description: metadata.description || "Subscription payment",
      customerId: pr.customer || "—",
      subscriptionId: pr.subscription || "—",
    };
  }, [data]);

  if (!r) return null;

  const downloadPDF = () => {
    const input = receiptRef.current;
    html2canvas(input, {
      scale: 2,
      ignoreElements: (element) => element.classList.contains("no-print"),
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const newWidth = imgWidth * ratio;
      const newHeight = imgHeight * ratio;

      pdf.addImage(imgData, "PNG", 0, 0, newWidth, newHeight);
      pdf.save(`LexMom-Subscription_${r.refNumber}.pdf`);
    });
  };

  const formatAmount = (amount) => {
    if (r.currency === "USD") {
      return usd(amount);
    }
    return inr(amount);
  };

  return (
    <Box ref={receiptRef} sx={{ px: "1rem", width: "100%" }}>
      <Box
        sx={{
          overflow: "hidden",
          p: "1rem",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          gap: "1rem",
          borderRadius: "1rem",
          width: "100%",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "8rem",
              height: "2.5rem",
              borderRadius: "0.5rem",
              padding: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={logo}
              alt="LexMom Logo"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
          <Tooltip className="no-print" title="Download Receipt">
            <IconButton
              aria-label="download"
              size="small"
              onClick={downloadPDF}
            >
              <DownloadRounded />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Amount Section */}
        <Box>
          <Box
            sx={{
              borderTop: "1px dashed #d1d5db",
              borderBottom: "1px dashed #d1d5db",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "2rem",
              p: "0.5rem",
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  color: "text.secondary",
                }}
              >
                Customer
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                {r.payerName}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Paid To
            </Typography>
            <Box>
              <Box
                sx={{
                  width: "7.5rem",
                  height: "2.5rem",
                  borderRadius: "0.5rem",
                  padding: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={logo}
                  alt="LexMom Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "0.5rem",
                borderTop: "none",
                borderLeft: "1px dashed #d1d5db",
                borderRight: "1px dashed #d1d5db",
                borderBottom: "1px dashed #d1d5db",
                borderRadius: "0.5rem",
                py: "0.4rem",
                px: "0.6rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "text.secondary",
                }}
              >
                Total Amount :
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: "1.3rem", fontWeight: "600" }}
              >
                {formatAmount(r.totalAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Payment Details */}
        <Box>
          <Box>
            <DetailBox title="Receipt Number" value={r.receiptNumber} full />
            <DetailBox title="Payment ID" value={r.refNumber} toLong={true} />
            <DetailBox title="Customer ID" value={r.customerId} />
            <DetailBox title="Subscription ID" value={r.subscriptionId} />
            <DetailBox title="Payment Time" value={r.paymentTime} />
            <DetailBox title="Payment Method" value={r.paymentMethod} />
            <DetailBox title="Status" value={r.status} />
            <DetailBox title="Currency" value={r.currency} />
          </Box>

          {/* Subscription Details */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box
              sx={{
                borderRadius: "0.5rem",
                border: "1px dashed #d1d5db",
                p: "0.5rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "primary.main",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Subscription Details
              </Typography>
              <Box>
                <DetailBox title="Plan" value={r.plan} full />
                <DetailBox title="Billing Cycle" value={r.billingCycle} full />
                <DetailBox title="Order ID" value={r.orderId} full />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "end",
                  fontSize: "0.7rem",
                  wordBreak: "break-word",
                  color: "text.secondary",
                }}
              >
                {r.description}
              </Typography>
            </Box>

            {/* Customer Details */}
            <Box
              sx={{
                borderRadius: "0.5rem",
                border: "1px dashed #d1d5db",
                p: "0.5rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "primary.main",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Customer Details
              </Typography>
              <Box>
                <DetailBox title="Customer Name" value={r.payerName} full />
                <DetailBox title="Email" value={r.email} full />
                <DetailBox title="Phone Number" value={r.mobile} full />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px dashed #d1d5db",
            pt: "0.5rem",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
            }}
          >
            Thank you for your subscription!
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              color: "text.secondary",
            }}
          >
            This is an computer-generated receipt. No signature required.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Original Razorpay Receipt Component (renamed)
const RazorpayReceiptCard = ({ data, background }) => {
  const receiptRef = useRef();
  const r = useMemo(() => {
    if (!data) return null;

    const pr = data.payment_response || {};
    const order = data.order || {};
    const notes = pr.notes || {};
    const upi = pr.upi || {};

    // Amount - note: your JSON shows amount as "1.00" (string) which is 1 rupee
    const amountInr = data.amount
      ? Number(data.amount)
      : pr.amount != null
        ? pr.amount / 100
        : Number(order.total_amount || 0);

    // Fee
    const feeInr = pr.fee != null ? pr.fee / 100 : 0;

    // Payment Method
    let payMethod = "";
    if (pr.method === "upi") {
      const vpa = pr.vpa || upi.vpa;
      payMethod = `UPI ${vpa ? `(${vpa})` : ""}`.trim();
    } else {
      payMethod = pr.method || data.payment_method || "—";
    }

    // Paid At
    const paidAt = data.paid_at || order.created_at;
    const paidAtStr = paidAt
      ? new Date(paidAt).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

    // Lawyer/Customer Name - get from order.lawyer
    const lawyer = order?.lawyer || {};
    const payerName =
      lawyer.first_name || lawyer.last_name
        ? `${lawyer.first_name || ""} ${lawyer.last_name || ""}`.trim()
        : lawyer.reminder_mail || lawyer.email || "—";

    const mobile = lawyer.mobile || "—";
    const email = lawyer.reminder_mail || lawyer.email || "—";

    // Bank/Network for UPI
    const bankOrNetwork =
      upi.payer_account_type || (pr.method === "upi" ? "UPI" : "—");

    return {
      totalAmount: amountInr,
      fee: feeInr,
      currency: pr.currency || "INR",
      refNumber: data.payment_id || pr.id || "—",
      paymentTime: paidAtStr,
      paymentMethod: payMethod,
      payerName,
      mobile,
      email,
      bankOrNetwork,
      receiptNumber: `RCP-${String(
        data.payment_id || pr.id || Date.now(),
      ).slice(-8)}`,
      orderId: order.id || notes.order_id || "—",
      status: (data.status || pr.status || "—").toLowerCase(),
      contact: pr.contact || mobile || "—",
      plan:
        order?.item?.subscription_plan?.name || notes.subscription_plan || "—",
      billingCycle: order?.billing_cycle || notes.billing_cycle || "—",
      description: notes.description || "Subscription payment",
      // New fields specific to your data structure
      orignalAmount: data.orignal_amount
        ? Number(data.orignal_amount)
        : amountInr,
      couponApplied: data.coupon_codes || "None",
      isTestPayment: data.is_test_payment || false,
    };
  }, [data]);

  if (!r) return null;

  const downloadPDF = () => {
    const input = receiptRef.current;
    html2canvas(input, {
      scale: 2,
      ignoreElements: (element) => element.classList.contains("no-print"),
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const newWidth = imgWidth * ratio;
      const newHeight = imgHeight * ratio;

      pdf.addImage(imgData, "PNG", 0, 0, newWidth, newHeight);
      pdf.save(`LexMom-Subscription_${r.refNumber}.pdf`);
    });
  };

  return (
    <Box ref={receiptRef} sx={{ px: "1rem" }}>
      <Box
        sx={{
          overflow: "hidden",
          p: "1rem",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          gap: "1rem",
          borderRadius: "1rem",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "8rem",
              height: "2.5rem",
              borderRadius: "0.5rem",
              padding: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={logo}
              alt="LexMom Logo"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
          <Tooltip className="no-print" title="Download Receipt">
            <IconButton
              aria-label="download"
              size="small"
              onClick={() => downloadPDF()}
            >
              <DownloadRounded />
            </IconButton>
          </Tooltip>
        </Box>

        <Box>
          <Box
            sx={{
              borderTop: "1px dashed #d1d5db",
              borderBottom: "1px dashed #d1d5db",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "2rem",
              p: "0.5rem",
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  color: "text.secondary",
                }}
              >
                Lawyer
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                {r.payerName}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Pay To
            </Typography>
            <Box>
              <Box
                sx={{
                  width: "7.5rem",
                  height: "2.5rem",
                  borderRadius: "0.5rem",
                  padding: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={logo}
                  alt="LexMom Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "0.5rem",
                borderTop: "none",
                borderLeft: "1px dashed #d1d5db",
                borderRight: "1px dashed #d1d5db",
                borderBottom: "1px dashed #d1d5db",
                borderRadius: "0.5rem",
                py: "0.4rem",
                px: "0.6rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: "600",
                  color: "text.secondary",
                }}
              >
                Total Amount :
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: "1.3rem", fontWeight: "600" }}
              >
                {inr(r.totalAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Box>
            <DetailBox title="Receipt Number" value={r.receiptNumber} full />
            <DetailBox title="Payment ID" value={r.refNumber} />
            <DetailBox title="Payment Time" value={r.paymentTime} />
            <DetailBox
              title="Payment Method"
              value={
                r.paymentMethod + (r.cardLast4 ? ` ••••${r.cardLast4}` : "")
              }
            />
            <DetailBox title="Status" value={r.status} />
            <DetailBox title="Bank / Network" value={r.bankOrNetwork} />
            <DetailBox title="Admin Fee" value={inr(r.fee)} />
            <DetailBox title="Original Amount" value={inr(r.orignalAmount)} />
            <DetailBox title="Discount Applied" value={r.couponApplied} />
            <DetailBox title="Final Amount" value={inr(r.totalAmount)} />
            {isDevMode && (
              <DetailBox
                title="Test Payment"
                value={r.isTestPayment ? "Yes" : "No"}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box
              sx={{
                borderRadius: "0.5rem",
                border: "1px dashed #d1d5db",
                p: "0.5rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "primary.main",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Subscription Details
              </Typography>

              <Box>
                <DetailBox title="Plan" value={r.plan} full />
                <DetailBox title="Billing Cycle" value={r.billingCycle} full />
                <DetailBox title="Order ID" value={r.orderId} full />
                <DetailBox
                  title="Original Price"
                  value={inr(r.orignalAmount)}
                  full
                />
                <DetailBox title="Discount" value={r.couponApplied} full />
                <DetailBox
                  title="Amount Paid"
                  value={inr(r.totalAmount)}
                  full
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "end",
                  fontSize: "0.7rem",
                  wordBreak: "break-word",
                  color: "text.secondary",
                }}
              >
                {r.description}
              </Typography>
            </Box>
            {/* Customer Details */}
            <Box
              sx={{
                borderRadius: "0.5rem",
                border: "1px dashed #d1d5db",
                p: "0.5rem",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "primary.main",
                  fontSize: "1rem",
                  fontWeight: "600",
                }}
              >
                Customer Details
              </Typography>
              <Box>
                <DetailBox title="Customer Name" value={r.payerName} full />
                <DetailBox title="Phone Number" value={r.mobile} full />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px dashed #d1d5db",
            pt: "0.5rem",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
            }}
          >
            Thank you for your subscription!
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              color: "text.secondary",
            }}
          >
            This is an computer-generated receipt. No signature required.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Reusable DetailBox component
function DetailBox({ title, value, full = false, toLong = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1rem",
        mb: "0.4rem",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontSize: "0.8rem",
          fontWeight: "500",
          color: "text.secondary",
          whiteSpace: "nowrap",
        }}
      >
        {title}:
      </Typography>

      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: "600",
          wordBreak: toLong ? "break-all" : "normal",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}
